import {useEffect, useState} from 'react';
import useSetupSessionQuery from 'src/api/graphql/useSetupSessionQuery';
import {useSessionContext} from 'src/state/usePayinSessionContext';
import {isMobile} from 'src/utils/utils';
import {isInAppBrowser} from 'src/v2/util/isInAppBrowser';
import {notifyInContextComplete} from 'src/v2/util/inContextCompletion';
import {urlRedirect} from '@stripe-internal/safe-links';
import {getStore} from 'src/v2/state/store';
import {getLogger} from 'src/utils/logger';

const POLL_INTERVAL_MS = 2000;

const logger = getLogger();

export const useSetupPolling = (): {
  isPolling: boolean;
  startPolling: () => void;
  stopPolling: () => void;
} => {
  const [isPolling, setIsPolling] = useState(false);
  const {clientSecret, sessionId} = useSessionContext();

  const setupSessionData = useSetupSessionQuery({
    variables: {
      input: {
        crypto_setup_session: sessionId,
        client_secret: clientSecret,
      },
    },
    skip: !isPolling || !sessionId || !clientSecret,
    pollInterval: POLL_INTERVAL_MS,
    component: 'useSetupPolling',
  });
  const store = getStore();
  const returnUrl = store.useSelector(
    (state) => state.mandateDetails.returnUrl,
  );
  const activeWallet = store.useSelector(
    (state) => state.walletConnection.activeWallet,
  );
  const disabledWalletProviders = store.useSelector(
    (state) => state.disabledWalletProviders,
  );

  const isMobileDevice = isMobile();
  const inAppBrowser = isInAppBrowser(activeWallet, disabledWalletProviders);

  const autoRedirectDisabled = isMobileDevice && inAppBrowser;

  const stopPolling = () => {
    setIsPolling(false);
  };

  const startPolling = () => {
    setIsPolling(true);
  };

  // Each time setupSessionData is polled, check if the session is successful or failed or errored
  // If so, stop polling and redirect to the appropriate screen
  useEffect(() => {
    if (isPolling && setupSessionData?.data?.v1CryptoInternalSetupSession) {
      const sessionState =
        setupSessionData.data.v1CryptoInternalSetupSession.state;

      store.setValue((state) => {
        state.state = sessionState;
      });

      if (sessionState === 'setup_complete') {
        // If this is being rendered in-context, we render success screen
        // We can't automatically redirect on mobile devices, we render success screen instead
        if (notifyInContextComplete() || autoRedirectDisabled) {
          store.setValue((state) => {
            state.screen = 'SUCCESS';
          });
        } else {
          // We won't render success screen when redirect. Emit a log event to track the completion.
          logger.log('crypto_payin_session.screen.rendered', {
            screen: 'SUCCESS',
            message: 'redirect for setup session',
          });
          urlRedirect(returnUrl);
        }
      } else if (sessionState === 'failed' || sessionState === 'canceled') {
        store.setValue((state) => {
          state.screen = sessionState === 'failed' ? 'ERROR' : 'CANCELED';
        });
      }
    }
  }, [isPolling, setupSessionData, autoRedirectDisabled, store, returnUrl]);

  return {
    isPolling,
    startPolling,
    stopPolling,
  };
};
